/**
 * components/riskTerminal/useWatchlist.js
 *
 * upstox-054: the watchlist arrives over the live stream (liveStream.js,
 * `watchlist` events, ≤ 1/s while prices move) — no polling while it is up.
 * One GET /upstox/watchlist at the start and on refresh (after a change to a
 * list); while the stream is down for 30 s ("polling"), GET every 2 s until
 * it returns. The last good body is kept through a failed GET; the failure
 * is reported.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribe } from './liveStream';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const WATCH_POLL_MS = 2000;

export default function useWatchlist() {
  const [watch, setWatch] = useState(null);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const inFlight = useRef(false);

  const poll = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await fetch(`${API}/upstox/watchlist`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 401 ? 'signed out' : 'HTTP ' + res.status);
      setWatch(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message || 'network error');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    const off = subscribe({
      watchlist: w => { setWatch(w); setError(null); },
      state: s => setPolling(s.mode === 'polling'),
    });
    poll();
    return off;
  }, [poll]);

  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(poll, WATCH_POLL_MS);
    return () => clearInterval(t);
  }, [polling, poll]);

  const refresh = useCallback(() => poll(), [poll]);
  return { watch, watchError: error, refreshWatch: refresh };
}
